// First-join flow per plan §9. Runs the *first* time a device
// authenticates against a family record. Always wholesale-replaces the
// local tree with the server snapshot — even when the server is empty.
// The first device joining an empty family ends up with an empty tree;
// the parent then adds profiles via Settings, and each save flows
// through the normal `notifyTreeChange` → outbox → PB pipe. The
// historical "empty server → push local up as the seed" path was
// retired in #18 (it was the source of #12, #15, and contributed to
// #16). A future merge-mode variant slots into the discriminated
// `FirstJoinResult` union when we get there.

import {
  deleteDailyProgress,
  listProgressKeys,
  saveDailyProgress,
  saveSyncedTree,
} from '../../lib/storage/indexedDb';
import { CURRENT_SCHEMA_VERSION } from '../../lib/storage/migrations';
import { dateKey as todayDateKey } from '../../lib/dateKey';
import type { DailyProgress, SyncedTree } from '../../lib/types';
import { useDeviceStore } from '../../store/device';
import { useSyncedStore } from '../../store/synced';
import { emitProgress } from '../progress/progressEvents';
import type { SyncClient } from './client';
import {
  applyRemoteToProgress,
  applyRemoteToTree,
  type ProfileRoutineRow,
  type ProfileRow,
  type ProgressRow,
  type RoutineRow,
  type TaskRow,
} from './mapping';

export type FirstJoinResult = { kind: 'replaced'; profileCount: number };

export async function firstJoin(client: SyncClient): Promise<FirstJoinResult> {
  const familyId = client.currentFamilyId();
  if (!familyId) {
    throw new Error('firstJoin requires an authenticated client');
  }

  const filter = `family = "${familyId}"`;
  const [profiles, routines, tasks, junction, progress] = await Promise.all([
    client.getFullList<ProfileRow>('profiles', filter),
    client.getFullList<RoutineRow>('routines', filter),
    client.getFullList<TaskRow>('tasks', filter),
    client.getFullList<ProfileRoutineRow>('profile_routines', filter),
    client.getFullList<ProgressRow>('progress', filter),
  ]);

  console.info('[sync:firstJoin] start', {
    familyId,
    profilesOnServer: profiles.length,
    routinesOnServer: routines.length,
    tasksOnServer: tasks.length,
    profileRoutinesOnServer: junction.length,
  });

  const result = await replaceLocal(profiles, routines, tasks, junction, progress);
  console.info(`[sync:firstJoin] result ${result.kind}`, result);
  return result;
}

async function replaceLocal(
  profileRows: ProfileRow[],
  routineRows: RoutineRow[],
  taskRows: TaskRow[],
  junctionRows: ProfileRoutineRow[],
  progressRows: ProgressRow[],
): Promise<FirstJoinResult> {
  const reconstructed = applyRemoteToTree({
    profiles: profileRows,
    routines: routineRows,
    tasks: taskRows,
    profileRoutines: junctionRows,
  });

  const local = useSyncedStore.getState().tree;
  const tree: SyncedTree = {
    schemaVersion: local?.schemaVersion ?? CURRENT_SCHEMA_VERSION,
    syncCode: local?.syncCode ?? null,
    settings: local?.settings ?? { soundOn: true },
    meta: local?.meta ?? {
      createdAt: new Date().toISOString(),
      lastWriteAt: new Date().toISOString(),
      lastWriteByDeviceId: 'unknown',
    },
    profiles: reconstructed.profiles,
    routines: reconstructed.routines,
    // Custom themes stay local — remote sync (§tasks/46) doesn't ship
    // them yet, so preserve whatever the local device already had.
    customThemes: local?.customThemes ?? [],
  };

  // Persist + commit to the synced store. Persisting the IDB blob
  // synchronously (vs. waiting on the debounced flush) means a crash
  // immediately after firstJoin doesn't lose the snapshot.
  await saveSyncedTree(tree);
  useSyncedStore.getState().setTree(tree);

  // Wipe any local progress: the previously-locally-built tree's
  // profile ids are gone; their progress keys are dead weight that
  // would never be read again.
  const stale = await listProgressKeys();
  for (const key of stale) {
    const match = key.match(/^aamusankari:progress:(.+?)_(\d{4}-\d{2}-\d{2})$/);
    if (!match) continue;
    const [, profileId, dateKey] = match;
    if (!profileId || !dateKey) continue;
    await deleteDailyProgress(profileId, dateKey);
  }

  // Write the server snapshot's progress for whichever profile the
  // device currently has active (per-day). Other-day or other-profile
  // progress lands lazily as those views open.
  const activeProfileId = useDeviceStore.getState().activeProfileId;
  const today = todayDateKey();
  if (activeProfileId) {
    const todaysRows = progressRows.filter(
      (r) => r.profile === activeProfileId && r.date_key === today,
    );
    const reconstructedProgress = applyRemoteToProgress(todaysRows);
    if (reconstructedProgress) {
      await saveDailyProgress(reconstructedProgress);
      emitProgress(reconstructedProgress);
    }
  }

  return { kind: 'replaced', profileCount: reconstructed.profiles.length };
}

// Test-only helper exported for direct construction of a DailyProgress
// argument in firstJoin tests.
export type { DailyProgress };
